import {
  Button,
  ButtonGroup,
  Card,
  Choice,
  DataStatus,
  Label,
  RadioItem,
  showNotification,
} from 'platform/components';
import {Clickable, Heading, Hide, HStack, Show, Space, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {filter, not} from 'ramda';
import {isString} from 'ramda-adjunct';

import {getOptionsFromServiceCaseOrderVariants} from '@dms/api/utils';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseOrdersQuery,
  useGetServiceCaseOrderVariantsQuery,
  usePostServiceOrderMutation,
  usePostServiceRequestCopyToServiceOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {isNonEditableServiceOrderState} from '@dms/shared-aftersales';

import {LiteralUnionAutocomplete, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {RequestOrder} from './RequestOrder';

interface CopyRequestProps extends RequiredTestIdProps {
  serviceCaseId: string;
  items: {
    serviceJobId: string;
    serviceOrderId: string;
  }[];
  onCopied: () => void;
  onClose: () => void;
}

export function CopyRequest(props: CopyRequestProps) {
  const [selectedOrder, setSelectedOrder] = useState<LiteralUnionAutocomplete<'NEW'> | null>(null);
  const [selectedSC, setSelectedSC] = useState<string | null>(null);
  const [variant, setVariant] = useState<number | string[] | string | null>(null);

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId: props.serviceCaseId});
  const {
    data: serviceCaseOrders,
    isLoading,
    isError,
  } = useGetServiceCaseOrdersQuery({
    serviceCaseId: props.serviceCaseId,
  });
  const {data: orderVariants} = useGetServiceCaseOrderVariantsQuery({
    serviceCaseId: props.serviceCaseId,
  });
  const [copyJobsToOtherOrder, {isLoading: isCopyJobsToOtherOrderLoading}] =
    usePostServiceRequestCopyToServiceOrderMutation();
  const [postServiceOrder, {isLoading: isPostServiceOrderLoading}] = usePostServiceOrderMutation();

  const handleCopyRequest = () => {
    if (!selectedOrder) {
      return;
    }

    if (selectedOrder === 'NEW') {
      if (!isString(variant)) {
        return;
      }

      postServiceOrder({serviceCaseId: props.serviceCaseId, body: {orderVariantId: variant}})
        .unwrap()
        .then((order) => {
          copyJobsToOtherOrder({
            serviceCaseId: order?.serviceCaseId ?? '',
            serviceOrderId: order?.id ?? '',
            body: props.items,
          })
            .unwrap()
            .then(showNotification.success)
            .then(props.onCopied)
            .then(props.onClose)
            .catch(handleApiError);
        })
        .catch(handleApiError);
      return;
    }
    copyJobsToOtherOrder({
      serviceCaseId: selectedSC ?? '',
      serviceOrderId: selectedOrder,
      body: props.items,
    })
      .unwrap()
      .then(showNotification.success)
      .then(props.onCopied)
      .then(props.onClose)
      .catch(handleApiError);
  };

  const serviceCaseOrdersToShow = filter(
    (order) =>
      not(props.items.some((item) => item.serviceOrderId === order?.serviceOrderId)) &&
      not(isNonEditableServiceOrderState(order?.state)),
    serviceCaseOrders ?? []
  );

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={23}>
      <VStack spacing={4}>
        <Text color="tertiary" size="small">
          {i18n.t('entity.order.labels.copyRequestDescription')}
        </Text>
        {serviceCaseOrdersToShow?.map((order, index) => (
          <Show key={order?.serviceOrderId} when={order}>
            <RequestOrder
              order={order}
              isSelected={selectedOrder === order?.serviceOrderId}
              onSelect={() => {
                setSelectedOrder(order?.serviceOrderId ?? null);
                setSelectedSC(props.serviceCaseId ?? null);
              }}
              data-testid={suffixTestId(`requestOrder-[${index}]`, props)}
            />
          </Show>
        ))}
        <Hide when={serviceCase?.state === 'HANDED_OVER'}>
          <Card variant="inlineWhite">
            <Clickable
              onClick={() => setSelectedOrder('NEW')}
              data-testid={suffixTestId('selectNew', props)}
            >
              <HStack spacing={3} align="center">
                <RadioItem
                  value={selectedOrder === 'NEW'}
                  data-testid={suffixTestId('selected', props)}
                />
                <Heading size={4}>{i18n.t('entity.order.labels.newServiceOrder')}</Heading>
              </HStack>
            </Clickable>
            <Show when={selectedOrder === 'NEW'}>
              <Space vertical={4} />
              <Label>{i18n.t('entity.order.labels.orderVariant')}</Label>
              <Choice
                value={variant}
                onChange={setVariant}
                options={getOptionsFromServiceCaseOrderVariants(orderVariants)}
                placeholder={i18n.t('general.labels.select')}
                menuInPortal
                data-testid={suffixTestId('variant', props)}
              />
            </Show>
          </Card>
        </Hide>
        <ButtonGroup align="right">
          <Button
            variant="secondary"
            onClick={props.onClose}
            data-testid={suffixTestId('cancel', props)}
            title={i18n.t('general.actions.cancel')}
          />
          <Button
            onClick={handleCopyRequest}
            isDisabled={!selectedOrder || (selectedOrder === 'NEW' && !variant)}
            isLoading={isCopyJobsToOtherOrderLoading || isPostServiceOrderLoading}
            data-testid={suffixTestId('confirm', props)}
            title={i18n.t('general.actions.confirm')}
          />
        </ButtonGroup>
      </VStack>
    </DataStatus>
  );
}
